import React, { useCallback, useEffect, useState } from "react";
import api, { getRequest, patchRequest, postRequest } from "../../apis/ApiInstence";
import AdminTable from "../../common/AdminTable";
import { useNavigate } from "react-router-dom";
import { FaEye, FaTrash, FaStar } from "react-icons/fa";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const ORDER_POLL_INTERVAL_MS = 120000;

const MyOrder = () => {
  const [order, setOrder] = useState([]);
  const [error, setError] = useState("");
  const [debugInfo, setDebugInfo] = useState("");
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [reviewProducts, setReviewProducts] = useState([]);
  const [loadingReviewProducts, setLoadingReviewProducts] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    description: "",
    imageFile: null,
    imagePreviewUrl: "",
    submitting: false,
    message: "",
  });
  const [openingInvoiceId, setOpeningInvoiceId] = useState(null);

  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);

  const getUserId = () => {
    try {
      const stored = JSON.parse(localStorage.getItem("user")) || {};
      if (
        stored?.id ||
        stored?._id ||
        stored?.customer_id ||
        stored?.user_id ||
        stored?.customerId ||
        stored?.userId
      ) {
        return (
          stored?.id ||
          stored?._id ||
          stored?.customer_id ||
          stored?.user_id ||
          stored?.customerId ||
          stored?.userId
        );
      }
    } catch {
      // ignore
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return (
        decoded?.id ||
        decoded?.user_id ||
        decoded?.customer_id ||
        decoded?.customerId ||
        decoded?.userId ||
        null
      );
    } catch {
      return null;
    }
  };

  const customerID = getUserId();
  const reviewStorageKey = (id) => `order_review_${id}`;
  const hasSubmittedReview = (id) => Boolean(localStorage.getItem(reviewStorageKey(id)));
  const saveProductReview = ({ productId, rating, description, imagePreviewUrl, review_id }) => {
    if (!productId) return;
    const storageKey = `product_review_${productId}`;
    let existing = [];
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey));
      if (Array.isArray(parsed)) existing = parsed;
      else if (parsed && typeof parsed === "object") existing = [parsed];
    } catch {
      existing = [];
    }
    const name = getReviewerName();
    const payload = {
      product_id: String(productId),
      name,
      rating,
      description,
      imagePreviewUrl: imagePreviewUrl || "",
      review_id: review_id || null,
      updatedAt: new Date().toISOString(),
    };
    const index = existing.findIndex(
      (item) =>
        String(item?.product_id || "") === String(productId) && String(item?.name || "") === name,
    );
    if (index >= 0) {
      existing[index] = { ...existing[index], ...payload };
    } else {
      existing.push(payload);
    }
    localStorage.setItem(storageKey, JSON.stringify(existing));
  };
  const getAuthHeaders = () => {
    const latestToken = localStorage.getItem("token");
    return latestToken ? { Authorization: `Bearer ${latestToken}` } : {};
  };
  const normalizeStatus = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
  const isDeliveredStatus = (value) => {
    const status = normalizeStatus(value);
    if (!status) return false;
    return [
      "delivered",
      "deliveredbyadmin",
      "deliveredsuccess",
      "deliverycomplete",
      "completed",
      "complete",
      "success",
      "deliverydone",
      "orderdelivered",
      "deliveredtocustomer",
    ].some((token) => status.includes(token));
  };

  const getReviewerName = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      return user?.name || user?.email || user?.mobile || "Customer";
    } catch {
      return "Customer";
    }
  };

  const mapProductsFromPayload = (payload) => {
    const candidates = [
      payload?.itemData1,
      payload?.items,
      payload?.order_items,
      payload?.orderDetails,
      payload?.data?.itemData1,
      payload?.data?.items,
      payload?.data?.order_items,
      payload?.data?.orderDetails,
    ];

    const rawList = candidates.find((arr) => Array.isArray(arr) && arr.length) || [];
    return rawList
      .map((item) => ({
        productId: String(
          item?.product_id ||
            item?.productId ||
            item?.productID ||
            item?.product?.id ||
            item?.product?.product_id ||
            item?.id ||
            "",
        ),
        productName:
          item?.product?.name ||
          item?.product_name ||
          item?.name ||
          `Product ${item?.product_id || item?.productId || ""}`,
      }))
      .filter((item) => item.productId);
  };

  const fetchReviewProducts = async (orderRow) => {
    const idsToTry = [orderRow?.id, orderRow?.order_id]
      .map((v) => String(v || "").trim())
      .filter(Boolean);
    const uniqueIds = [...new Set(idsToTry)];

    for (const id of uniqueIds) {
      try {
        const res = await getRequest(`order/order_details/${encodeURIComponent(id)}`, {
          headers: getAuthHeaders(),
        });
        const mapped = mapProductsFromPayload(res?.data || {});
        if (mapped.length) return mapped;
      } catch (err) {
        // continue trying alternate ids
      }
    }
    return [];
  };

  const fetchOrders = useCallback(async () => {
    try {
      setError("");
      if (!customerID) {
        setError("User ID not found. Please login again.");
        setDebugInfo("customerID is null/undefined");
        setOrder([]);
        return;
      }
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authorization token missing. Please login again.");
        setOrder([]);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const safeId = encodeURIComponent(String(customerID));

      let res = await api.get(`order/${safeId}`, { headers });
      if (!Array.isArray(res?.data)) {
        res = await api.get(`orderlist/${safeId}`, { headers });
      }

      if (Array.isArray(res?.data)) {
        setOrder(res.data);
        setDebugInfo("");
      } else {
        setOrder([]);
        setDebugInfo("Response is not an array");
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Order fetch failed";
      setError(message);
      const status = err?.response?.status;
      const url =
        err?.config?.baseURL && err?.config?.url
          ? `${err.config.baseURL}${err.config.url}`
          : err?.config?.url || "unknown url";
      setDebugInfo(`status=${status || "n/a"} url=${url}`);
      console.error("Order fetch failed:", err);
    }
  }, [customerID]);

  const handleCancelOrder = async (id) => {
    if (!id) return;

    const confirmResult = await MySwal.fire({
      title: "Are you sure?",
      text: "Do you want to cancel this order?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No",
    });

    if (confirmResult.isConfirmed) {
      try {
        const response = await patchRequest(`/order/${id}`, { status: "cancelByUser" });
        MySwal.fire({
          title: "Cancelled!",
          text: response.data.message,
          icon: "success",
        });
        fetchOrders();
      } catch (err) {
        MySwal.fire({
          title: "Failed!",
          text: err.response?.data?.message || "Could not cancel the order.",
          icon: "error",
        });
      }
    }
  };

  const openReviewModal = async (orderRow) => {
    if (!isDeliveredStatus(orderRow?.status)) {
      await MySwal.fire({
        icon: "info",
        title: "Review Available After Delivery",
        text: "Please submit your review once the order is delivered.",
      });
      return;
    }
    const saved = localStorage.getItem(reviewStorageKey(orderRow.id));
    let parsed = null;
    try {
      parsed = saved ? JSON.parse(saved) : null;
    } catch {
      parsed = null;
    }

    setActiveOrder(orderRow);
    setLoadingReviewProducts(true);
    setReviewProducts([]);
    setReviewForm({
      rating: Number(parsed?.rating) || 0,
      description: parsed?.description || "",
      imageFile: null,
      imagePreviewUrl: parsed?.imagePreviewUrl || "",
      submitting: false,
      message: "",
    });
    setIsReviewOpen(true);

    try {
      const mapped = await fetchReviewProducts(orderRow);
      setReviewProducts(mapped);
      if (!mapped.length) {
        setReviewForm((prev) => ({
          ...prev,
          message: "No product details found for this order right now.",
        }));
      }
    } catch (err) {
      setReviewForm((prev) => ({ ...prev, message: "Product list load failed for this order" }));
      console.error("Review products fetch failed:", err?.response?.data || err);
    } finally {
      setLoadingReviewProducts(false);
    }
  };

  const closeReviewModal = () => {
    setIsReviewOpen(false);
    setActiveOrder(null);
    setReviewProducts([]);
    setLoadingReviewProducts(false);
    setReviewForm({
      rating: 0,
      description: "",
      imageFile: null,
      imagePreviewUrl: "",
      submitting: false,
      message: "",
    });
  };

  const handleReviewImage = (file) => {
    if (!file) {
      setReviewForm((prev) => ({ ...prev, imageFile: null, imagePreviewUrl: "" }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setReviewForm((prev) => ({
        ...prev,
        imageFile: file,
        imagePreviewUrl: reader.result || "",
      }));
    };
    reader.readAsDataURL(file);
  };

  const submitReview = async () => {
    if (loadingReviewProducts) {
      setReviewForm((prev) => ({ ...prev, message: "Products are still loading, please wait..." }));
      return;
    }
    let selectedProductId = String(
      reviewProducts[0]?.productId || activeOrder?.product_id || activeOrder?.productId || ""
    ).trim();
    const rating = Number(reviewForm.rating || 0);
    const description = String(reviewForm.description || "").trim();

    if (!selectedProductId && activeOrder?.id) {
      try {
        const mapped = await fetchReviewProducts(activeOrder);
        if (mapped.length) {
          setReviewProducts(mapped);
          selectedProductId = String(mapped[0].productId || "").trim();
        }
      } catch (err) {
        console.error("Fallback product fetch failed:", err?.response?.data || err);
      }
    }

    if (!selectedProductId) {
      setReviewForm((prev) => ({
        ...prev,
        message: "Product details unavailable for this order. Please try again in a moment.",
      }));
      return;
    }
    if (!rating) {
      setReviewForm((prev) => ({ ...prev, message: "Please select rating" }));
      return;
    }

    let savedReview = null;
    try {
      savedReview = JSON.parse(localStorage.getItem(reviewStorageKey(activeOrder?.id)));
    } catch {
      savedReview = null;
    }
    const alreadySubmitted = Boolean(savedReview);
    setReviewForm((prev) => ({ ...prev, submitting: true, message: "" }));

    try {
      const formData = new FormData();
      formData.append("product_id", selectedProductId);
      formData.append("name", getReviewerName());
      formData.append("rating", String(rating));
      formData.append("message", description || "Rating submitted");
      if (reviewForm.imageFile) {
        formData.append("image", reviewForm.imageFile);
      }

      const resolveExistingReviewId = async () => {
        if (savedReview?.review_id || savedReview?.id) return savedReview?.review_id || savedReview?.id;
        try {
          const res = await getRequest(`reviews?product_id=${encodeURIComponent(selectedProductId)}&status=active`);
          const list = Array.isArray(res?.data) ? res.data : [];
          const name = getReviewerName();
          const match = list.find((item) => String(item?.name || "").trim() === name);
          return match?.id || null;
        } catch (err) {
          if (err?.response?.status === 404) {
            const res = await getRequest(`review?product_id=${encodeURIComponent(selectedProductId)}&status=active`);
            const list = Array.isArray(res?.data) ? res.data : [];
            const name = getReviewerName();
            const match = list.find((item) => String(item?.name || "").trim() === name);
            return match?.id || null;
          }
          return null;
        }
      };

      let reviewId = null;

      if (alreadySubmitted) {
        reviewId = await resolveExistingReviewId();
        if (reviewId) {
          await patchRequest(`reviews/${reviewId}`, formData, {
            headers: {
              ...getAuthHeaders(),
            },
          });
        }
      } else {
        const tryPost = async (url) =>
          postRequest(url, formData, {
            headers: {
              ...getAuthHeaders(),
            },
          });

        try {
          const res = await tryPost("reviews");
          reviewId = res?.data?.id || res?.data?.review?.id || null;
        } catch (err) {
          if (err?.response?.status === 404) {
            const res = await tryPost("review");
            reviewId = res?.data?.id || res?.data?.review?.id || null;
          } else {
            throw err;
          }
        }
      }

      const localPayload = {
        product_id: selectedProductId,
        rating,
        description,
        imagePreviewUrl: reviewForm.imagePreviewUrl || "",
        review_id: reviewId || savedReview?.review_id || savedReview?.id || null,
      };
      localStorage.setItem(reviewStorageKey(activeOrder?.id), JSON.stringify(localPayload));
      saveProductReview(localPayload);
      setReviewForm((prev) => ({ ...prev, submitting: false, message: "Review saved" }));
    } catch (err) {
      const isUnauthorized = err?.response?.status === 401;
      const msg = isUnauthorized
        ? "Session expired. Please login again."
        : err?.response?.data?.message || err?.response?.data || "Review submit failed";
      setReviewForm((prev) => ({ ...prev, submitting: false, message: String(msg) }));
      return;
    }

    await MySwal.fire({
      icon: "success",
      title: "Review Saved",
      text: alreadySubmitted
        ? "Your review has been updated locally."
        : "Your review has been submitted successfully.",
      timer: 1600,
      showConfirmButton: false,
    });
    closeReviewModal();
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return value;
    }
  };

  const formatAmount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return value || "-";
    return `Rs. ${num.toLocaleString("en-IN")}`;
  };

  const renderStatusBadge = (value) => (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold text-white ${
        value === "cancelByUser" ? "bg-red-500" : "bg-emerald-500"
      }`}
    >
      {value === "cancelByUser"
        ? "Cancelled By You"
        : String(value || "processing").charAt(0).toUpperCase() + String(value || "processing").slice(1)}
    </span>
  );

  const buildViewerUrl = (url) => {
    if (!url) return "";
    return `https://docs.google.com/viewerng/viewer?url=${encodeURIComponent(url)}`;
  };

  const extractInvoiceUrl = (payload) => {
    if (!payload || typeof payload !== "object") return "";
    const direct =
      payload?.invoice_url ||
      payload?.invoiceUrl ||
      payload?.url ||
      payload?.pdf ||
      payload?.data?.invoice_url ||
      payload?.data?.invoiceUrl ||
      payload?.data?.url ||
      payload?.data?.pdf;
    if (typeof direct === "string" && direct.trim()) return direct.trim();
    const list =
      payload?.invoice_urls ||
      payload?.invoiceUrls ||
      payload?.data?.invoice_urls ||
      payload?.data?.invoiceUrls;
    if (Array.isArray(list) && list.length) {
      const first = list.find((entry) => typeof entry === "string" && entry.trim());
      if (first) return first.trim();
    }
    return "";
  };

  const buildInvoicePayload = (row) => {
    const shiprocketId =
      row?.shiprocket_order_id ||
      row?.shiprocketOrderId ||
      row?.shiprocket_id ||
      row?.sr_order_id ||
      row?.srOrderId ||
      row?.shiprocket_orderid;
    const orderId = row?.order_id || row?.orderId || row?.id;
    const payload = {};
    if (shiprocketId) {
      payload.id = shiprocketId;
      payload.shiprocket_order_id = shiprocketId;
      payload.shiprocketOrderId = shiprocketId;
    }
    if (orderId) {
      payload.order_id = orderId;
      payload.orderId = orderId;
      payload.id = payload.id || orderId;
    }
    return Object.keys(payload).length ? payload : null;
  };

  const handleOpenInvoice = async (row) => {
    const payload = buildInvoicePayload(row);
    if (!payload) {
      await MySwal.fire({
        icon: "info",
        title: "Invoice Not Ready",
        text: "Invoice id not found for this order yet.",
      });
      return;
    }

    try {
      setOpeningInvoiceId(row?.id);
      const res = await postRequest("shiprocket/invoice", payload);
      const url = extractInvoiceUrl(res?.data || res);
      if (!url) throw new Error("Invoice URL not returned.");
      const viewerUrl = buildViewerUrl(url);
      if (viewerUrl) {
        window.open(viewerUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to open invoice right now.";
      await MySwal.fire({
        icon: "error",
        title: "Invoice Error",
        text: String(msg),
      });
    } finally {
      setOpeningInvoiceId(null);
    }
  };

  const renderActionButtons = (row) => (
    <div className="flex flex-nowrap items-center justify-center gap-2 sm:flex-wrap">
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          handleOpenInvoice(row);
        }}
        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 px-3 py-2 text-xs font-semibold text-white shadow-md transition-all duration-300 hover:from-emerald-600 hover:to-green-700 sm:px-4 sm:text-sm"
        aria-disabled={openingInvoiceId === row?.id}
      >
        <FaEye className="text-sm" />
        <span className="hidden sm:inline">
          {openingInvoiceId === row?.id ? "Opening..." : "View"}
        </span>
      </a>

      <button
        onClick={() => openReviewModal(row)}
        disabled={!isDeliveredStatus(row.status)}
        title={
          isDeliveredStatus(row.status)
            ? "Review your order"
            : "Review available after delivery"
        }
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white shadow-md transition-all duration-300 sm:px-4 sm:text-sm ${
          hasSubmittedReview(row.id)
            ? "bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700"
            : "bg-gradient-to-r from-[#F36F21] to-[#E63946] hover:from-[#e45f13] hover:to-[#d92a38]"
        } ${!isDeliveredStatus(row.status) ? "cursor-not-allowed opacity-50" : ""}`}
      >
        <FaStar className="text-sm" />
        <span className="hidden sm:inline">
          {hasSubmittedReview(row.id) ? "Edit Review" : "Review"}
        </span>
      </button>

      <button
        onClick={() => handleCancelOrder(row.id)}
        disabled={row.status === "cancelByUser"}
        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-red-500 to-red-600 px-3 py-2 text-xs font-semibold text-white shadow-md transition-all duration-300 hover:from-red-600 hover:to-red-700 sm:px-4 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FaTrash className="text-sm" />
        <span className="hidden sm:inline">Cancel</span>
      </button>
    </div>
  );

  const tableHeaders = [
    { Header: "Order ID", accessor: "order_id" },
    {
      Header: "Address",
      accessor: "shipping_address",
      Cell: ({ value }) => <span className="text-slate-700">{value || "-"}</span>,
    },
    {
      Header: "Amount",
      accessor: "total_amount",
      Cell: ({ value }) => <span className="font-semibold text-[#1f3c88]">{formatAmount(value)}</span>,
    },
    {
      Header: "Date",
      accessor: "createdAt",
      Cell: ({ value }) => <span className="text-slate-600">{formatDate(value)}</span>,
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => renderStatusBadge(value),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => renderActionButtons(row.original),
    },
  ];

  useEffect(() => {
    if (!customerID) return;
    let pollId = null;

    const stopPolling = () => {
      if (pollId) {
        clearInterval(pollId);
        pollId = null;
      }
    };

    const startPolling = () => {
      if (pollId || document.visibilityState === "hidden") return;
      pollId = setInterval(() => {
        if (document.visibilityState === "visible") {
          fetchOrders();
        }
      }, ORDER_POLL_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchOrders();
        startPolling();
      } else {
        stopPolling();
      }
    };

    fetchOrders();
    handleVisibilityChange();
    window.addEventListener("focus", fetchOrders);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      stopPolling();
      window.removeEventListener("focus", fetchOrders);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [customerID, fetchOrders]);

  if (!order) return <div className=" text-center">Loading your order details...</div>;

  return (
    <div className="">
      <div className="">
        <h2 className="lg:mb-6 text-center text-2xl font-bold" style={{ color: "#351F72" }}>
          My Order List
        </h2>

        {error ? (
          <div>
            <p className="text-red-500">{error}</p>
            {debugInfo && <p className="mt-2 text-xs text-gray-400">{debugInfo}</p>}
          </div>
        ) : order.length > 0 ? (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_8px_22px_rgba(15,23,42,0.08)]">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
              Total Orders: {order.length}
            </div>
            <div className="hidden p-2 sm:p-3 lg:block">
              <AdminTable tableHeaders={tableHeaders} tableData={order} />
            </div>

            <div className="space-y-3 lg:hidden">
              {order.map((row) => (
                <div
                  key={row.id}
                  className="rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_6px_18px_rgba(15,23,42,0.06)]"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs text-slate-500">Order ID</p>
                      <p className="text-sm font-bold text-[#351F72]">{row.order_id || "-"}</p>
                    </div>
                    {renderStatusBadge(row.status)}
                  </div>

                  <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-2">
                    <div>
                      <p className="text-[11px] text-slate-500">Amount</p>
                      <p className="text-sm font-semibold text-slate-800">{formatAmount(row.total_amount)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500">Date</p>
                      <p className="text-sm font-semibold text-slate-800">{formatDate(row.createdAt)}</p>
                    </div>
                  </div>

                  <div className="mt-2">
                    <p className="text-[11px] text-slate-500">Shipping Address</p>
                    <p className="text-sm text-slate-700">{row.shipping_address || "-"}</p>
                  </div>

                  <div className="mt-3">{renderActionButtons(row)}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p>No orders available</p>
        )}
      </div>

      {isReviewOpen && activeOrder && (
        <div className="fixed inset-0 z-[2500] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-bold text-[#351F72]">Write a Review</h3>
                <p className="text-sm text-slate-500">Order #{activeOrder.order_id}</p>
              </div>
              <button
                type="button"
                onClick={closeReviewModal}
                className="rounded-full border border-slate-300 px-2.5 py-1 text-sm text-slate-700"
              >
                x
              </button>
            </div>

            <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <p className="font-semibold">How was your experience?</p>
              <p className="text-xs text-slate-500">
                Rate and describe your experience. Image upload optional hai.
              </p>
            </div>

            <div className="mb-4 rounded-lg border border-[#e3e9ff] bg-[#f7f9ff] px-3 py-2 text-xs text-slate-700">
              Review Product: <span className="font-semibold">{reviewProducts[0]?.productName || "Detecting..."}</span>
            </div>

            <div className="mb-4">
              <p className="mb-2 text-sm font-semibold text-slate-700">Your Rating</p>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={`review-star-${star}`}
                    type="button"
                    onClick={() => setReviewForm((prev) => ({ ...prev, rating: star, message: "" }))}
                    className="text-2xl"
                  >
                    <i
                      className={`fa-star ${
                        star <= reviewForm.rating ? "fa-solid text-yellow-500" : "fa-regular text-slate-300"
                      }`}
                    ></i>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <p className="mb-2 text-sm font-semibold text-slate-700">Description</p>
              <textarea
                rows={4}
                value={reviewForm.description}
                onChange={(e) => setReviewForm((prev) => ({ ...prev, description: e.target.value, message: "" }))}
                placeholder="Write your experience (optional)..."
                className="w-full rounded-xl border border-slate-300 p-3 text-sm outline-none focus:border-[#351F72]"
              />
            </div>

            <div className="mb-4">
              <p className="mb-2 text-sm font-semibold text-slate-700">Add Image (Optional)</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleReviewImage(e.target.files?.[0])}
                className="w-full rounded-xl border border-slate-300 p-2 text-sm"
              />
              {reviewForm.imagePreviewUrl && (
                <img
                  src={reviewForm.imagePreviewUrl}
                  alt="Review upload preview"
                  className="mt-3 h-24 w-24 rounded-lg border border-slate-200 object-cover"
                />
              )}
            </div>

            {reviewForm.message && <p className="mb-3 text-sm text-red-500">{reviewForm.message}</p>}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeReviewModal}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReview}
                disabled={reviewForm.submitting || loadingReviewProducts}
                className="rounded-lg bg-gradient-to-r from-[#F36F21] to-[#E63946] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingReviewProducts
                  ? "Loading..."
                  : reviewForm.submitting
                    ? "Saving..."
                    : "Submit Review"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrder;
